import MondayIcon from 'monday-ui-react-core/dist/Icon';
import Button from 'monday-ui-react-core/dist/Button';
import AlertIcon from 'monday-ui-react-core/dist/icons/Alert';
import SendIcon from 'monday-ui-react-core/dist/icons/Send';
import styled from 'styled-components';
import UpdatedDialog from '../UpdatedDialog';
import { FileContext } from '../../context/FileContext';
import { useContext, useMemo } from 'react';
import APP_CONFIG from '../../config';
import { isFeatureAvailable, FEATURES } from '../../constants/featureBasedPricing';

const AlertDiv = styled.div`
  padding: 0px 20px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;

  .text-base {
    font-size: 1rem /* 16px */;
    line-height: 1.5rem;
  }

  .text-lg {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  .text-2xl {
    font-size: 1.5rem; /* 24px */
    line-height: 2rem;
  }
  .text-center {
    text-align: center;
  }

  .font-bold {
    font-weight: 700;
  }
  .font-semibold {
    font-weight: 500;
  }

  .font-normal {
    font-weight: 400;
  }

  .mt-8 {
    margin-top: 2rem;
  }
  .mt-4 {
    margin-top: 1rem;
  }
  .mb-4 {
    margin-bottom: 1rem;
  }
  .mb-8 {
    margin-bottom: 2rem;
  }
  .ml-auto {
    margin-left: auto;
  }
  .mr-auto {
    margin-right: auto;
  }
  .flex {
    display: flex;
  }
  .justify-end {
    justify-content: flex-end;
  }
  .gap-4 {
    gap: 1rem; /* 16px */
  }
  .max-w-500 {
    max-width: 500px;
  }

  .cursor-pointer {
    cursor: pointer;
  }
  .font-bold {
    font-weight: 700;
  }
  .underline {
    text-decoration: underline;
  }
`;
const EmailNotVerifiedDialog = ({
  showEmailConfirmationDialog,
  setShowEmailConfirmationDialog,
}) => {
  return (
    <UpdatedDialog
      handleClose={() =>
        setShowEmailConfirmationDialog({ show: false, email: '' })
      }
      dialogBody={
        <AlertDiv>
          <MondayIcon
            iconType={MondayIcon.type.SVG}
            icon={AlertIcon}
            iconSize={50}
            style={{
              color: 'var(--negative-color)',
            }}
          />
          <div className="text-2xl font-semibold mb-8 text-center">
            Your email address is not verified
            <p className="mt-8 break-words font-normal text-lg">
              We sent a verification email to
              {showEmailConfirmationDialog.email}. Once your email is verified
              you can start sending documents for eSignatures. Not getting the
              verification email? Click below to change email address.
            </p>
          </div>
          <Button
            onClick={() =>
              setShowEmailConfirmationDialog({ show: false, email: '' })
            }
          >
            Close
          </Button>
        </AlertDiv>
      }
    />
  );
};


const OtherPreviewPageDialog = ({ closeDialog, type }) => {

  const { pricingPlan, slug } = useContext(FileContext);
  const premiumFeatureText = useMemo(() => {
    if (!pricingPlan) return '';
    const unavailableFeatures = [];
    if (!isFeatureAvailable(pricingPlan, FEATURES.MULTIPLE_TEMPLATES)) unavailableFeatures.push('Multiple templates');
    if (!isFeatureAvailable(pricingPlan, FEATURES.LINE_ITEMS)) unavailableFeatures.push('using Sub-Items');
    if (!isFeatureAvailable(pricingPlan, FEATURES.AUTO_SEND)) unavailableFeatures.push('Auto-Send documents');
    if (!isFeatureAvailable(pricingPlan, FEATURES.PAYMENT)) unavailableFeatures.push('collecting payment via stripe');
    if (!isFeatureAvailable(pricingPlan, FEATURES.FILLABLE_FORMS)) unavailableFeatures.push('fillable forms');
    if (!isFeatureAvailable(pricingPlan, FEATURES.SIGNING_ORDER)) unavailableFeatures.push('signing order');
    if (!isFeatureAvailable(pricingPlan, FEATURES.OTP)) unavailableFeatures.push('OTP password protection');
    let featuresText = '';
    if (unavailableFeatures.length > 1) {
      featuresText = `${unavailableFeatures.slice(0, -1).join(', ')} and ${unavailableFeatures.slice(-1)}`;
    } else if (unavailableFeatures.length === 1) {
      featuresText = unavailableFeatures[0];
    }
    return unavailableFeatures.length > 0
      ? `${featuresText} ${unavailableFeatures.length === 1 ? 'is' : 'are'} not available. Learn more about our`
      : 'Learn more about our';
  }, [pricingPlan]);

  return (
    <UpdatedDialog
      minWidth="30%"
      handleClose={closeDialog}
      dialogBody={
        <AlertDiv>
          {type === 'usingPremiumFields' ? (
            <div className="text-base font-bold">
              Some features are not available with your current plan.
              <div className="mt-8 font-normal">
                {premiumFeatureText}
                <span
                  className="cursor-pointer font-bold underline"
                  onClick={() =>
                    window.open('https://getsign.io/getsign-pricing/', '_blank')
                  }
                >
                  pricing plans
                </span>
                .
              </div>
              <div className="ml-auto mt-8 flex justify-end gap-4">
                <Button kind={Button.kinds.TERTIARY} onClick={closeDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    window.open(
                      `https://${slug}.monday.com/apps/installed_apps/10050849`,
                      '_blank',
                    );
                  }}
                >
                  Upgrade
                </Button>
              </div>
            </div>
          ) : type === 'missingEmailColValue' ? (
            <div className="text-center max-w-500 mb-8">
              <MondayIcon
                iconType={MondayIcon.type.SVG}
                icon={AlertIcon}
                iconSize={50}
                style={{
                  color: 'var(--negative-color)',
                }}
                className="mb-8"
              />
              <div className="text-base font-semibold mb-8">
                We couldn't send this document for signing. Sorry, we seem to be
                missing the recipient's email address 🤷‍♂️
                <div className="mt-4 font-normal">
                  Please provide an email address before proceeding with the
                  sending process.
                </div>
              </div>

              <Button onClick={closeDialog}>Cancel</Button>
            </div>
          ) : type === 'accessTokenIssue' ? (
            <div className="text-center max-w-500 mb-8">
              <MondayIcon
                iconType={MondayIcon.type.SVG}
                icon={AlertIcon}
                iconSize={50}
                style={{
                  color: 'var(--negative-color)',
                }}
                className="mb-8"
              />
              <div className="text-base font-semibold mb-8">
                We couldn't send this document for signing. Please click on
                repair button which is located in settings of GetSign item-view
                to fix this issue.
              </div>

              <Button onClick={closeDialog}>Cancel</Button>
            </div>
          ) : type === 'missingRequiredReceiverSigFields' ? (
            <>
              <MondayIcon
                iconType={MondayIcon.type.SVG}
                icon={AlertIcon}
                iconSize={50}
                style={{
                  color: 'var(--negative-color)',
                }}
              />
              <div className="text-2xl font-semibold text-center mb-4">
                Missing signature fields
                <div className="mt-8 text-base font-normal">
                  Please fill add signature fields for recipient.
                </div>
              </div>
              <Button onClick={closeDialog}>Cancel</Button>
            </>
          ) : type === 'Sent' ? (
            <div className="mb-8 text-center">
              <MondayIcon
                iconType={MondayIcon.type.SVG}
                icon={SendIcon}
                iconSize={50}
                style={{
                  color: 'var(--primary-color)',
                }}
              />
              <div className="text-2xl gap-4 mb-8 text-center font-semibold">
                Sent
                <div className="text-base mt-4  font-normal">
                  Document sent to signer successfully.
                </div>
              </div>
              <Button
                onClick={() => {
                  closeDialog();
                  window.close();
                }}
              >
                Done
              </Button>
            </div>
          ) : type === 'Resent' ? (
            <div className="mb-8 text-center">
              <MondayIcon
                iconType={MondayIcon.type.SVG}
                icon={SendIcon}
                iconSize={50}
                style={{
                  color: 'var(--primary-color)',
                }}
              />
              <div className="text-2xl gap-4 mb-8 text-center font-semibold">
                Resent
                <div className="text-base mt-4 font-normal">
                  Document resent to signer successfully.
                </div>
              </div>
              <Button
                onClick={() => {
                  closeDialog();
                  window.close();
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="mb-8 text-center">
              <MondayIcon
                iconType={MondayIcon.type.SVG}
                icon={AlertIcon}
                iconSize={50}
                style={{
                  color: 'var(--negative-color)',
                }}
                className="mb-8"
              />
              <div className="text-2xl font-semibold mb-8 text-center">
                There are missing fields for this recipient
                <div className="mt-4 font-normal text-base">
                  Please fill out all the required details on the settings menu.
                </div>
              </div>

              <Button onClick={closeDialog}>Cancel</Button>
            </div>
          )}
        </AlertDiv>
      }
    />
  );
};

export { EmailNotVerifiedDialog, OtherPreviewPageDialog };
